import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { connectedFileReadSessionId } from "@/lib/connectedFileRead";
import { connectedMessageOwnerSession, validateConnectedMessageJournal } from "@/lib/connectedMessageExecutor";
import { loadConnectorWrite, type PendingConnectorWrite } from "@/lib/connectorPendingWrite";
import { subscribeConnectedMessageJournalChanged } from "@/lib/connectedMessageJournalBus";
import { useConnectedMessageWrite } from "@/hooks/useConnectedMessageWrite";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";
import { useLanguage } from "@/i18n/LanguageProvider";

export interface ConnectedMessagePendingRecoveryPanelProps {
  ownerId: string;
  sessionId: string | null;
  isCurrent?: () => boolean;
  onRecovered: (entry: PendingConnectorWrite, isCurrent: () => boolean) => Promise<boolean>;
}
export function ConnectedMessagePendingRecoveryPanel(props: ConnectedMessagePendingRecoveryPanelProps) {
  const [entry, setEntry] = useState<PendingConnectorWrite | null>(null);
  const expectedSid = useRef<string | null>(null);
  const generation = useRef(0), currentOrigin = useRef(props.isCurrent);
  currentOrigin.current = props.isCurrent;
  useEffect(() => {
    const epoch = ++generation.current, lifecycle = generation;
    setEntry(null); expectedSid.current = null;
    const current = () => lifecycle.current === epoch && (currentOrigin.current?.() ?? true);
    const load = async () => {
      try {
        if (!props.sessionId || !current()) return;
        const active = await connectedMessageOwnerSession(props.ownerId);
        const stored = await loadConnectorWrite(props.ownerId, "connected_message");
        await connectedMessageOwnerSession(props.ownerId, active.sid);
        if (!current()) return;
        if (!stored || !validateConnectedMessageJournal(stored, props.ownerId) || stored.body.auth_session_id !== active.sid ||
          stored.body.mirror_context?.session_id !== props.sessionId) { setEntry(null); return; }
        expectedSid.current = active.sid; setEntry(stored);
      } catch { if (current()) setEntry(null); }
    };
    void load();
    const unsubscribe = subscribeConnectedMessageJournalChanged(owner => { if (owner === props.ownerId) void load(); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user.id !== props.ownerId || expectedSid.current && connectedFileReadSessionId(session.access_token) !== expectedSid.current) {
        lifecycle.current++; setEntry(null);
      }
    });
    return () => { lifecycle.current++; unsubscribe(); subscription.unsubscribe(); };
  }, [props.ownerId, props.sessionId]);
  if (!entry || !props.sessionId) return null;
  return <OwnedConnectedMessageRecovery key={entry.body.idempotency_key} {...props} sessionId={props.sessionId} entry={entry} />;
}
function OwnedConnectedMessageRecovery({ ownerId, sessionId, isCurrent, onRecovered, entry }: ConnectedMessagePendingRecoveryPanelProps & { sessionId: string; entry: PendingConnectorWrite }) {
  const { t } = useLanguage(), surfaceQuota = useQuotaErrorSurface();
  const actionId = (entry.body.mirror_context as { action_id: string }).action_id;
  const write = useConnectedMessageWrite(ownerId, actionId, sessionId, surfaceQuota, isCurrent);
  const [ackBusy, setAckBusy] = useState(false), [ackPending, setAckPending] = useState(false), [complete, setComplete] = useState(false);
  const lock = useRef(false);
  const recover = async () => {
    if (lock.current || !write.ready) return;
    lock.current = true; setAckBusy(true);
    const current = write.captureCurrent();
    try {
      const proven = await write.recover();
      if (!proven || !current()) return;
      setAckPending(true);
      if (await onRecovered(structuredClone(proven), current) !== true || !current()) return;
      if (!await write.acknowledge() || !current()) return;
      setAckPending(false); setComplete(true);
    } catch { if (current()) setAckPending(true); }
    finally { lock.current = false; if (current()) setAckBusy(false); }
  };
  if (complete) return null;
  return <section aria-label={t("connectedMessageSend.recoveryTitle")} className="space-y-2 rounded-xl border border-border bg-card p-3">
    <p className="text-sm font-semibold">{t("connectedMessageSend.recoveryTitle")}</p>
    <p className="break-words text-sm text-muted-foreground">{entry.preview?.title || t("proactive.untitled")}</p>
    <p className="text-xs text-muted-foreground">{t(ackPending ? "connectedMessageSend.ackPending" : "connectedMessageSend.recoveryHint")}</p>
    {write.error && <p role="alert" className="text-xs text-destructive">{t(write.error === "invalid_session" ? "mirror.connector.accountChanged" : write.error === "connected_message_safe_retry" ? "connectedMessageSend.safeRetry" : "mirror.connector.outcomeUnknown")}</p>}
    <button type="button" disabled={!write.ready || write.busy || ackBusy} onClick={() => void recover()} className="min-h-11 rounded-lg border border-border px-3 text-sm font-medium">{t(ackPending ? "connectedMessageSend.ackRetry" : "mirror.connector.checkResult")}</button>
  </section>;
}
